import { Button } from '../../components/ui/button';
import {
  DataTable,
  DataTableColumn,
  FilterField,
} from '../../components/ui/data-table';
import { Badge } from '../../components/ui/badge';
import InlineFilter from '../../components/ui/inline-filter';
import ActionDropdown from '../common/action-dropdown';
import { getStatusColor } from '../../utils/helpers';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../components/ui/card';
import { Filter, Download } from 'lucide-react';

export interface MarketingTool {
  id: string;
  type: 'referral-link' | 'promo-banner' | 'campaign' | 'utm-link';
  name: string;
  referralUrl: string;
  landingPage: string;
  clicks: number;
  registrations: number;
  deposits: string;
  conversionRate: string;
  status: 'active' | 'inactive' | 'pending';
  createdDate: string;
}

interface MarketingCampaignsProps {
  data: MarketingTool[];
  searchValue: string;
  onSearchChange: (value: string) => void;
  onSearchReset: () => void;
  showFilters: boolean;
  onToggleFilters: () => void;
  filterFields: FilterField[];
  activeFilters: { [key: string]: string };
  onFilterChange: (filters: { [key: string]: string }) => void;
  onFiltersClear: () => void;
  onFiltersApply: () => void;
  onExport: () => void;
}

const MarketingCampaigns = ({
  data,
  searchValue,
  onSearchChange,
  onSearchReset,
  showFilters,
  onToggleFilters,
  filterFields,
  activeFilters,
  onFilterChange,
  onFiltersClear,
  onFiltersApply,
  onExport,
}: MarketingCampaignsProps) => {
  const columns: DataTableColumn<MarketingTool>[] = [
    {
      id: 'type',
      header: 'Type',
      accessor: row => (
        <Badge className={getStatusColor(row.type)}>{row.type}</Badge>
      ),
      sortable: true,
      sortValue: row => row.type,
    },
    {
      id: 'name',
      header: 'Campaign Name',
      accessor: row => row.name,
      sortable: true,
      sortValue: row => row.name,
    },
    {
      id: 'referralUrl',
      header: 'Referral URL',
      accessor: row => (
        <code className="px-2 py-1 bg-muted rounded text-sm truncate max-w-xs block">
          {row.referralUrl}
        </code>
      ),
      sortable: true,
      sortValue: row => row.referralUrl,
    },
    {
      id: 'landingPage',
      header: 'Landing Page',
      accessor: row => (
        <code className="px-2 py-1 bg-muted rounded text-sm truncate max-w-xs block">
          {row.landingPage}
        </code>
      ),
      sortable: true,
      sortValue: row => row.landingPage,
    },
    {
      id: 'clicks',
      header: 'Clicks',
      accessor: row => row.clicks,
      sortable: true,
      sortValue: row => row.clicks,
    },
    {
      id: 'registrations',
      header: 'Registrations',
      accessor: row => row.registrations,
      sortable: true,
      sortValue: row => row.registrations,
    },
    {
      id: 'deposits',
      header: 'Deposits',
      accessor: row => row.deposits,
      sortable: true,
      sortValue: row => row.deposits,
    },
    {
      id: 'conversionRate',
      header: 'Conversion Rate',
      accessor: row => row.conversionRate,
      sortable: true,
      sortValue: row => row.conversionRate,
    },
    {
      id: 'createdDate',
      header: 'Created Date',
      accessor: row => row.createdDate,
      sortable: true,
      sortValue: row => row.createdDate,
    },
    {
      id: 'status',
      header: 'Status',
      accessor: row => (
        <Badge className={getStatusColor(row.status)}>{row.status}</Badge>
      ),
      sortable: true,
      sortValue: row => row.status,
    },
    {
      id: 'actions',
      header: 'Actions',
      accessor: row => (
        <ActionDropdown
          id={row.id}
          size="sm"
          variant="ghost"
          showView={true}
          showEdit={true}
          showDelete={false}
          showDeposit={false}
          showWithdraw={false}
          onView={() => console.log('View campaign:', row.id)}
          onEdit={() => console.log('Edit campaign:', row.id)}
        />
      ),
    },
  ];

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between gap-4 w-full">
          <div>
            <CardTitle>Marketing Campaigns</CardTitle>
            <CardDescription>
              View and manage all marketing assets
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={onToggleFilters}>
              <Filter className="h-4 w-4 mr-2" />
              {showFilters ? 'Hide Filter' : 'Show Filter'}
            </Button>
            <Button variant="outline" size="sm" onClick={onExport}>
              <Download className="h-4 w-4 mr-2" />
              Export
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <InlineFilter
          showFilters={showFilters}
          onToggle={onToggleFilters}
          filterFields={filterFields}
          activeFilters={activeFilters}
          onFilterChange={onFilterChange}
          onFiltersClear={onFiltersClear}
          onFiltersApply={onFiltersApply}
          title="Filter Marketing Tools"
        />
        <DataTable
          columns={columns}
          data={data}
          searchValue={searchValue}
          searchPlaceholder="Search marketing tools..."
          onSearchChange={onSearchChange}
          onSearchReset={onSearchReset}
          filterFields={filterFields}
          activeFilters={activeFilters}
          onFiltersChange={() => {}}
          showColumnVisibility={true}
          showAddButton={false}
          showExportButton={false}
          initialPageSize={10}
        />
      </CardContent>
    </Card>
  );
};

export default MarketingCampaigns;
